/**
 * OmniClinicalBridge Physician-in-the-Loop Context
 * OmniAlera Technologies Holdings Inc.
 * 
 * Core principle: AI ASSISTS, PHYSICIAN DECIDES
 * Never autonomous clinical decisions
 * 
 * This context manages:
 * - Clinical decision audit trails
 * - Physician verification checkpoints
 * - Override documentation
 * - Compliance logging
 */

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

// Types for clinical decisions
export interface ClinicalDecision {
  id: string;
  timestamp: Date;
  type: 'dosing' | 'diagnosis' | 'treatment' | 'referral' | 'prescription' | 'emergency';
  category: 'medication' | 'condition' | 'procedure' | 'consultation';
  description: string;
  aiRecommendation: string;
  aiConfidence: 'high' | 'moderate' | 'low';
  evidenceLevel: 'A' | 'B' | 'C';
  riskLevel: 'critical' | 'high' | 'moderate' | 'low';
  patientContext?: string; // Anonymized
  physicianAction: 'pending' | 'approved' | 'modified' | 'rejected' | 'deferred';
  physicianNotes?: string;
  physicianId?: string;
  verifiedAt?: Date;
  modificationDetails?: string;
  overrideReason?: string;
}

export interface VerificationCheckpoint {
  id: string;
  decisionId: string;
  checkpointType: 'dosing' | 'interaction' | 'contraindication' | 'critical';
  message: string;
  requiresConfirmation: boolean;
  confirmed: boolean;
  confirmedAt?: Date;
  confirmedBy?: string;
}

export interface AuditLogEntry {
  id: string;
  timestamp: Date;
  action: string;
  decisionId?: string;
  details: Record<string, any>;
  userId?: string;
  ipAddress?: string;
  userAgent?: string;
}

interface PhysicianInLoopContextType {
  // Decision management
  currentDecision: ClinicalDecision | null;
  pendingDecisions: ClinicalDecision[];
  decisionHistory: ClinicalDecision[];
  
  // Verification
  pendingCheckpoints: VerificationCheckpoint[];
  
  // Actions
  createDecision: (decision: Omit<ClinicalDecision, 'id' | 'timestamp' | 'physicianAction'>) => ClinicalDecision;
  approveDecision: (decisionId: string, notes?: string) => void;
  modifyDecision: (decisionId: string, modifications: string, notes?: string) => void;
  rejectDecision: (decisionId: string, reason: string) => void;
  deferDecision: (decisionId: string, reason: string) => void;
  
  // Checkpoints
  createCheckpoint: (checkpoint: Omit<VerificationCheckpoint, 'id' | 'confirmed'>) => void;
  confirmCheckpoint: (checkpointId: string) => void;
  
  // Audit
  auditLog: AuditLogEntry[];
  logAction: (action: string, details?: Record<string, any>) => void;
  
  // Override
  requestOverride: (decisionId: string) => Promise<boolean>;
  
  // Settings
  requireVerificationForCritical: boolean;
  setRequireVerificationForCritical: (value: boolean) => void;
}

const PhysicianInLoopContext = createContext<PhysicianInLoopContextType | undefined>(undefined);

// Generate unique IDs
const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

export const PhysicianInLoopProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentDecision, setCurrentDecision] = useState<ClinicalDecision | null>(null);
  const [pendingDecisions, setPendingDecisions] = useState<ClinicalDecision[]>([]);
  const [decisionHistory, setDecisionHistory] = useState<ClinicalDecision[]>([]);
  const [pendingCheckpoints, setPendingCheckpoints] = useState<VerificationCheckpoint[]>([]);
  const [auditLog, setAuditLog] = useState<AuditLogEntry[]>([]);
  const [requireVerificationForCritical, setRequireVerificationForCritical] = useState(true);
  
  // Log action to audit trail
  const logAction = useCallback((action: string, details: Record<string, any> = {}) => {
    const entry: AuditLogEntry = {
      id: generateId(),
      timestamp: new Date(),
      action,
      details,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined
    };
    
    setAuditLog(prev => [entry, ...prev].slice(0, 1000)); // Keep last 1000 entries
    
    // Also log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.log('[AUDIT]', action, details);
    }
  }, []);
  
  // Create a new clinical decision
  const createDecision = useCallback((
    decisionData: Omit<ClinicalDecision, 'id' | 'timestamp' | 'physicianAction'>
  ): ClinicalDecision => {
    const decision: ClinicalDecision = {
      ...decisionData,
      id: generateId(),
      timestamp: new Date(),
      physicianAction: 'pending'
    };
    
    setPendingDecisions(prev => [decision, ...prev]);
    setCurrentDecision(decision);
    
    logAction('DECISION_CREATED', {
      decisionId: decision.id,
      type: decision.type,
      riskLevel: decision.riskLevel,
      aiConfidence: decision.aiConfidence
    });
    
    // Auto-create checkpoint for critical decisions
    if (decision.riskLevel === 'critical' && requireVerificationForCritical) {
      createCheckpoint({
        decisionId: decision.id,
        checkpointType: 'critical',
        message: `CRITICAL: This ${decision.type} decision requires physician verification before proceeding.`,
        requiresConfirmation: true
      });
    }
    
    return decision;
  }, [logAction, requireVerificationForCritical]);
  
  // Approve a decision
  const approveDecision = useCallback((decisionId: string, notes?: string) => {
    setPendingDecisions(prev => {
      const decision = prev.find(d => d.id === decisionId);
      if (decision) {
        const approvedDecision: ClinicalDecision = {
          ...decision,
          physicianAction: 'approved',
          physicianNotes: notes,
          verifiedAt: new Date()
        };
        
        setDecisionHistory(history => [approvedDecision, ...history]);
        
        logAction('DECISION_APPROVED', {
          decisionId,
          notes,
          type: decision.type
        });
      }
      return prev.filter(d => d.id !== decisionId);
    });
    
    if (currentDecision?.id === decisionId) {
      setCurrentDecision(null);
    }
  }, [currentDecision, logAction]);
  
  // Modify a decision
  const modifyDecision = useCallback((decisionId: string, modifications: string, notes?: string) => {
    setPendingDecisions(prev => {
      const decision = prev.find(d => d.id === decisionId);
      if (decision) {
        const modifiedDecision: ClinicalDecision = {
          ...decision,
          physicianAction: 'modified',
          modificationDetails: modifications,
          physicianNotes: notes,
          verifiedAt: new Date()
        };
        
        setDecisionHistory(history => [modifiedDecision, ...history]);
        
        logAction('DECISION_MODIFIED', {
          decisionId,
          modifications,
          notes,
          originalRecommendation: decision.aiRecommendation
        });
      }
      return prev.filter(d => d.id !== decisionId);
    });
    
    if (currentDecision?.id === decisionId) {
      setCurrentDecision(null);
    }
  }, [currentDecision, logAction]);
  
  // Reject a decision
  const rejectDecision = useCallback((decisionId: string, reason: string) => {
    setPendingDecisions(prev => {
      const decision = prev.find(d => d.id === decisionId);
      if (decision) {
        const rejectedDecision: ClinicalDecision = {
          ...decision,
          physicianAction: 'rejected',
          overrideReason: reason,
          verifiedAt: new Date()
        };
        
        setDecisionHistory(history => [rejectedDecision, ...history]);
        
        logAction('DECISION_REJECTED', {
          decisionId,
          reason,
          type: decision.type,
          aiRecommendation: decision.aiRecommendation
        });
      }
      return prev.filter(d => d.id !== decisionId);
    });
    
    if (currentDecision?.id === decisionId) {
      setCurrentDecision(null);
    }
  }, [currentDecision, logAction]);
  
  // Defer a decision
  const deferDecision = useCallback((decisionId: string, reason: string) => {
    setPendingDecisions(prev => prev.map(d => 
      d.id === decisionId 
        ? { ...d, physicianAction: 'deferred' as const, physicianNotes: reason }
        : d
    ));
    
    logAction('DECISION_DEFERRED', { decisionId, reason });
  }, [logAction]);
  
  // Create verification checkpoint
  const createCheckpoint = useCallback((
    checkpointData: Omit<VerificationCheckpoint, 'id' | 'confirmed'>
  ) => {
    const checkpoint: VerificationCheckpoint = {
      ...checkpointData,
      id: generateId(),
      confirmed: false
    };
    
    setPendingCheckpoints(prev => [...prev, checkpoint]);
    
    logAction('CHECKPOINT_CREATED', {
      checkpointId: checkpoint.id,
      type: checkpoint.checkpointType,
      decisionId: checkpoint.decisionId
    });
  }, [logAction]);
  
  // Confirm checkpoint
  const confirmCheckpoint = useCallback((checkpointId: string) => {
    setPendingCheckpoints(prev => prev.map(cp =>
      cp.id === checkpointId
        ? { ...cp, confirmed: true, confirmedAt: new Date() }
        : cp
    ));
    
    logAction('CHECKPOINT_CONFIRMED', { checkpointId });
    
    // Remove confirmed checkpoints after a delay
    setTimeout(() => {
      setPendingCheckpoints(prev => prev.filter(cp => cp.id !== checkpointId));
    }, 500);
  }, [logAction]);
  
  // Request override (with documentation requirement)
  const requestOverride = useCallback(async (decisionId: string): Promise<boolean> => {
    logAction('OVERRIDE_REQUESTED', { decisionId });
    
    // In a real implementation, this might open a modal or require additional auth
    // For now, we just log it
    return true;
  }, [logAction]);
  
  const value: PhysicianInLoopContextType = {
    currentDecision,
    pendingDecisions,
    decisionHistory,
    pendingCheckpoints,
    createDecision,
    approveDecision,
    modifyDecision,
    rejectDecision,
    deferDecision,
    createCheckpoint,
    confirmCheckpoint,
    auditLog,
    logAction,
    requestOverride,
    requireVerificationForCritical,
    setRequireVerificationForCritical
  };
  
  return (
    <PhysicianInLoopContext.Provider value={value}>
      {children}
    </PhysicianInLoopContext.Provider>
  );
};

export const usePhysicianInLoop = (): PhysicianInLoopContextType => {
  const context = useContext(PhysicianInLoopContext);
  if (!context) {
    throw new Error('usePhysicianInLoop must be used within a PhysicianInLoopProvider');
  }
  return context;
};
